import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { Icons } from '@icons/icons';

import { ISearchInputProps } from './SearchInput.types';

import DynamicIconWrapper from '../DynamicIconWrapper';

import './SearchInput.scss';

const SearchInput: React.FC<ISearchInputProps> = ({
	classNames,
	containerClassname,
	delay = 1000,
	disabled,
	isAutoFocus,
	isClearable,
	isLoading,
	isNumber = false,
	hideSearch,
	onFocusIn,
	onSearch,
	onClear,
	placeholder,
	searchThreshold = 1,
	value = '',
	children,
}) => {
	const [localValue, setLocalValue] = useState(value);
	const timerId = useRef<NodeJS.Timeout | null>(null);
	const inputRef = useRef<HTMLInputElement | null>(null) as any;

	const classes = cn('f5gSearchInput', classNames);

	const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		if (isNumber && isNaN(Number(e.target.value))) return;

		clearTimeout(timerId.current as NodeJS.Timeout);

		setLocalValue(e.target.value);

		timerId.current = setTimeout(() => {
			if (e.target.value.length > searchThreshold) onSearch(e.target.value.trim());
			if (e.target.value.length === 0) onSearch(e.target.value);
		}, delay);

		if (e.target.value.length === 0 && onClear) onClear();
	}, [delay, isNumber, onClear, onSearch, searchThreshold]);

	const clearValue = () => {
		setLocalValue('');
		onSearch('');

		if (onClear) onClear();
	};

	useEffect(() => setLocalValue(value), [value]);

	useEffect(() => {
		if (!isLoading && isAutoFocus) inputRef?.current?.focus();
	}, [isLoading, isAutoFocus]);

	return (
		<div className={cn('f5gSearchInputContainer', containerClassname)}>
			<div
				className='f5gSearchInputIcon'
				onClick={hideSearch}
			>
				<DynamicIconWrapper
					icon={Icons.search}
					isDisabled={disabled}
				/>
			</div>
			<input
				ref={inputRef}
				aria-label='search-input'
				className={classes}
				disabled={disabled || isLoading}
				id='before'
				onChange={onInputChange}
				onFocus={onFocusIn}
				placeholder={placeholder}
				type='text'
				value={localValue}
			/>
			{!localValue && <label htmlFor='before'>{placeholder}</label>}
			<div className='f5gSearchInputInputsButtons'>
				{isClearable && localValue
					&& <span
						className='f5gSearchInputClearValue'
						data-testid='f5g-search-input-clear'
						onClick={clearValue}
					>
						{Icons.cross()}
					</span>
				}
				{children}
			</div>
		</div>
	);
};

export default SearchInput;
